import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  Checkbox,
  TableContainer, InputLabel, Select, MenuItem,
  CardContent,
  FormControl,
  Grid,
  TableHead,
  TextField,  Alert,
  Autocomplete,
  Container,
  Box,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { getClientList, postEmailTime, getEmailClients } from "src/api/analytics";
import { getClientProperties } from "src/api/ShiftMaster";
import Scrollbar from "src/components/Scrollbar";
import Page from "src/components/Page";
import DashboardBreadcrumbLink from 'src/components/DashboardBreadcrumbLink';
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const normalizeDisplayName = (name) =>
  String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const buildUniqueClientOptions = (clients) => {
  const grouped = new Map();

  (clients || []).forEach((clientItem) => {
    const key = normalizeDisplayName(clientItem?.displayname);
    if (!key) return;

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }

    grouped.get(key).push(clientItem);
  });

  return Array.from(grouped.values()).map((groupItems) => {
    if (groupItems.length === 1) return groupItems[0];

    const assignTargets = new Set(
      groupItems
        .map((item) => Number(item.AssignClient || 0))
        .filter((assignId) => assignId > 0)
    );

    const mappedMain = groupItems.find((item) => assignTargets.has(Number(item.ID)));
    if (mappedMain) return mappedMain;

    const rootEntry = groupItems.find((item) => Number(item.AssignClient || 0) === 0);
    return rootEntry || groupItems[0];
  });
};

const EmailTimings = ({ isReseller }) => {
  const theme = useTheme();
  const resellerClientType = (localStorage.getItem('client_type') || '').toUpperCase();
  const effectiveResellerClientType = resellerClientType === 'INDIA' ? 'INDIA' : 'US';
  const effectiveClientType = isReseller ? effectiveResellerClientType : 'US';
  const [clientInfo, setClientInfo] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientLocationOptions, setClientLocationOptions] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [clientLocationMap, setClientLocationMap] = useState({});
  const [clientData, setClientData] = useState([]);
  const [time, setTime] = useState('00:00');
  const [openAuto, setOpenAuto] = useState(false);
  const [openLocationAuto, setOpenLocationAuto] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [locationSelectAll, setLocationSelectAll] = useState(false);
  const [status, setStatusBase] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(false)
  const [locationErrorMessage, setLocationErrorMessage] = useState(false)
  const [emailType, setEmailType] = useState('daily');
  const [data,setData] = useState([])
  const [loadingLocations, setLoadingLocations] = useState(false);

  const uniqueClientOptions = useMemo(
    () => buildUniqueClientOptions(clientInfo),
    [clientInfo]
  );

  const selectedClientIds = useMemo(
    () => selectedClients
      .map((client) => Number(client?.ID || client?.id || 0))
      .filter((id) => id > 0),
    [selectedClients]
  );

  const selectedLocationOptions = useMemo(
    () => clientLocationOptions.filter((locationItem) =>
      selectedLocations.includes(locationItem.ID || locationItem.id)
    ),
    [clientLocationOptions, selectedLocations]
  );

  const clientsWithLocations = useMemo(
    () => selectedClientIds.filter((clientId) => (clientLocationMap[clientId] || []).length > 0),
    [selectedClientIds, clientLocationMap]
  );

  const clientsWithoutLocations = useMemo(
    () => selectedClientIds.filter((clientId) => (clientLocationMap[clientId] || []).length === 0),
    [selectedClientIds, clientLocationMap]
  );

  const selectedLocationParentIds = useMemo(
    () => new Set(
      selectedLocationOptions
        .map((locationItem) => Number(locationItem.parentClientId || 0))
        .filter((id) => id > 0)
    ),
    [selectedLocationOptions]
  );

  const hasUnselectedLocationClient = useMemo(
    () => clientsWithLocations.some((clientId) => !selectedLocationParentIds.has(clientId)),
    [clientsWithLocations, selectedLocationParentIds]
  );

  const payloadTargetIds = useMemo(() => {
    const locationIds = selectedLocationOptions
      .map((locationItem) => Number(locationItem.ID || locationItem.id || 0))
      .filter((id) => id > 0);

    return Array.from(new Set([...locationIds, ...clientsWithoutLocations]));
  }, [selectedLocationOptions, clientsWithoutLocations]);

  const handleEmailType = (event) => {
    setEmailType(event.target.value)
  }

  useEffect(() => {
    setErrorMessage(false);
    setLocationErrorMessage(false);
    if (loadingLocations) {
      return;
    }

    if (payloadTargetIds.length === 1 && !hasUnselectedLocationClient) {
      const data = {
        client_id: payloadTargetIds[0],
      };
      getEmailClients(data, (response) => {
        if (response.status === 200) {
          const filteredData = response.data.filter(item => item.Type === emailType);
          if (filteredData.length > 0 && filteredData[0].Time) {
            setTime(`${filteredData[0].Time}`)
          } else {
            setTime('00:00');
          }
        }
      });
    }
    else {
      setTime('00:00');
    }
  }, [payloadTargetIds, emailType, hasUnselectedLocationClient, loadingLocations]);

  useEffect(() => {
    setStatusBase(false);

    getClientList((response) => {
      if (response.status === 200) {
        const filteredClients = response.data.filter(client => client.ClientType === effectiveClientType && client.EnvironmentType!==0);
        const sortedData = [...filteredClients].sort((a, b) =>
          a.displayname.trim().localeCompare(b.displayname.trim())
        )
        setClientInfo(sortedData);
      }
    });

     const data = {
      client_id: '',
    };
    getEmailClients(data, (response) => {
      if (response.status === 200) {
        //const filteredData = response.data.filter(item => item.Type === emailType);
        setData(response.data);
      }
    });
  }, [effectiveClientType]);

  useEffect(() => {
    
    if(emailType){
      const result = data.filter(item => item.Type === emailType);
      setClientData(result);
    }
  }, [emailType,data])

  useEffect(() => {
    if (selectedClientIds.length === 0) {
      setLoadingLocations(false);
      setClientLocationOptions([]);
      setSelectedLocations([]);
      setLocationSelectAll(false);
      setClientLocationMap({});
      return;
    }

    let isMounted = true;
    setLoadingLocations(true);

    Promise.all(
      selectedClientIds.map(
        (clientId) =>
          new Promise((resolve) => {
            getClientProperties({ AssignClient: String(clientId) }, (response) => {
              const responseArray = Array.isArray(response.data)
                ? response.data
                : (response.data ? [response.data] : []);

              const locations = response.status === 200
                ? responseArray
                    .map((locationItem) => ({
                      ...locationItem,
                      ID: Number(locationItem?.ID ?? locationItem?.id ?? 0),
                      Location: String(locationItem?.Location || locationItem?.name || '').trim(),
                      parentClientId: clientId,
                    }))
                    .filter((locationItem) => locationItem.ID > 0)
                : [];

              resolve({ clientId, locations });
            });
          })
      )
    ).then((results) => {
      if (!isMounted) return;

      const nextClientLocationMap = {};
      results.forEach(({ clientId, locations }) => {
        nextClientLocationMap[clientId] = locations;
      });

      const uniqueLocations = Array.from(
        new Map(
          results
            .flatMap(({ locations }) => locations)
            .map((locationItem) => [locationItem.ID, locationItem])
        ).values()
      );

      setClientLocationMap(nextClientLocationMap);
      setClientLocationOptions(uniqueLocations);
      setSelectedLocations([]);
      setLocationSelectAll(false);
      setLoadingLocations(false);
    });

    return () => {
      isMounted = false;
    };
  }, [selectedClientIds]);

  const handleClientChange = (event, value) => {
    if (value.some(client => client.selectAll)) {
      if (selectAll) {
        setSelectedClients([]);
        setSelectAll(false);
      } else {
        setSelectedClients(uniqueClientOptions);
        setSelectAll(true);
      }
    } else {
      setSelectedClients(value);
      setSelectAll(value.length === uniqueClientOptions.length);
    }
    setLocationErrorMessage(false);
  };

  const handleLocationChange = (event, value) => {
    let nextSelectedLocations = [];

    if (value.some((locationItem) => locationItem.selectAll)) {
      nextSelectedLocations = locationSelectAll
        ? []
        : clientLocationOptions.map((locationItem) => locationItem.ID || locationItem.id);
    } else {
      nextSelectedLocations = value.map((locationItem) => locationItem.ID || locationItem.id);
    }

    setSelectedLocations(nextSelectedLocations);
    setLocationSelectAll(
      clientLocationOptions.length > 0 &&
      nextSelectedLocations.length === clientLocationOptions.length
    );
    if (nextSelectedLocations.length > 0) {
      setLocationErrorMessage(false);
    }
  };

  const handleTimeChange = (event) => {
    setTime(event.target.value);
  };

  const handleSave = () => {
    if (selectedClients.length === 0) {
      setErrorMessage(true);
      setLocationErrorMessage(false);
    }
    else if (loadingLocations) {
      return;
    }
    else if (clientsWithLocations.length > 0 && (selectedLocations.length === 0 || hasUnselectedLocationClient)) {
      setErrorMessage(false);
      setLocationErrorMessage(true);
    }
    else {
      setErrorMessage(false);
      setLocationErrorMessage(false);

      const data = payloadTargetIds.map((targetId) => ({
        ClientId: targetId,
        Time: time,
        Type: emailType
      }));

      postEmailTime(data, (response) => {
        if (response.status === 200) {
          const data = {
            client_id: '',
          };
          getEmailClients(data, (response) => {
            if (response.status === 200) {
              const filteredData = response.data.filter(item => item.Type === emailType);
              setClientData(filteredData);
              setStatusBase(true);
              setMessage("Timings Added successfully");
              setTime('00:00');
              setSelectedClients([]);
              setSelectedLocations([]);
              setClientLocationOptions([]);
              setClientLocationMap({});
              setSelectAll(false);
              setLocationSelectAll(false);
              setTimeout(() => {
                setStatusBase(false);
              }, 1000);
            }
          });
        }
      });
    }
  };

  const handleFilterOptions = (options, params) => {
    const { inputValue } = params;
    const filtered = options.filter(option => option.displayname.toLowerCase().includes(inputValue.toLowerCase()));
    return [{ displayname: 'Select All', selectAll: true }, ...filtered];
  };

  const handleLocationFilterOptions = (options, params) => {
    const { inputValue } = params;
    const filtered = options.filter(option =>
      (option.Location || '').toLowerCase().includes(inputValue.toLowerCase())
    );
    return [{ Location: 'Select All', selectAll: true }, ...filtered];
  };

  return (
    <>
      <Page title="Email Timings">
        <Container maxWidth="xxl" sx={{ p: 0 }}>
          <Box sx={{ mb: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h4">Email Timings</Typography>
            </Box>
            <Breadcrumbs separator="•" aria-label="breadcrumb">
              <DashboardBreadcrumbLink sx={{ fontSize: '0.875rem' }} />
              <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>Settings</Typography>
              <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>Email Timings</Typography>
            </Breadcrumbs>
          </Box>
          <Card variant="outlined" sx={{ p: 0 }}>
            <CardContent sx={{p:2}}>
              <Grid container spacing={2} mt={0}>

                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-labell">Email Type</InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={emailType}
                      label="Email Type"
                      onChange={handleEmailType}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>

                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      multiple
                      fullWidth
                      id="clients-autocomplete"
                      options={uniqueClientOptions}
                      value={selectedClients}
                      getOptionLabel={(option) => (option.selectall && selectedClients.length === uniqueClientOptions.length) ? 'Select All' : `${option.displayname || ''}`}
                      onChange={handleClientChange}
                      disableCloseOnSelect
                      open={openAuto}
                      onOpen={() => setOpenAuto(true)}
                      onClose={() => setOpenAuto(false)}
                      filterOptions={handleFilterOptions}
                      renderTags={() => null}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} key={option.ID}>
                          {option.selectAll ? (
                            <>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selectAll}
                              />
                              {option.displayname}
                            </>
                          ) : (
                            <>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selectedClients.some(client => client.ID === option.ID)}
                              />
                              {`${option.displayname || ''}`}
                            </>
                          )}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onFocus={() => setOpenAuto(true)}
                          label="Select Client"
                          placeholder="Select Client"
                          error={!!errorMessage}
                          helperText={
                            // Use helperText to prevent shifting
                            errorMessage ? (
                              <Box component="span" sx={{ minHeight: '24px', display: 'block' }}>
                                <Typography color="error" variant="body2" align="left" display="block" sx={{ mt: 0, mb: 0, lineHeight: '24px', fontSize: '0.875rem' }}>
                                  Please Select The Client
                                </Typography>
                              </Box>
                            ) : (
                              <Box component="span" sx={{ minHeight: '24px', display: 'block' }} />
                            )
                          }
                          FormHelperTextProps={{
                            sx: { m: 0, minHeight: '24px', lineHeight: '24px', display: 'block' }
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                {clientLocationOptions.length > 0 && (
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <Autocomplete
                        multiple
                        fullWidth
                        id="locations-autocomplete"
                        options={clientLocationOptions}
                        value={selectedLocationOptions}
                        getOptionLabel={(option) => option.selectAll ? 'Select All' : `${option.Location || ''}`}
                        onChange={handleLocationChange}
                        disableCloseOnSelect
                        open={openLocationAuto}
                        onOpen={() => setOpenLocationAuto(true)}
                        onClose={() => setOpenLocationAuto(false)}
                        filterOptions={handleLocationFilterOptions}
                        renderTags={() => null}
                        renderOption={(props, option) => (
                          <li {...props} key={option.ID || option.Location}>
                            {option.selectAll ? (
                              <>
                                <Checkbox
                                  icon={icon}
                                  checkedIcon={checkedIcon}
                                  style={{ marginRight: 8 }}
                                  checked={locationSelectAll}
                                />
                                {option.Location}
                              </>
                            ) : (
                              <>
                                <Checkbox
                                  icon={icon}
                                  checkedIcon={checkedIcon}
                                  style={{ marginRight: 8 }}
                                  checked={selectedLocations.includes(option.ID || option.id)}
                                />
                                {option.Location}
                              </>
                            )}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            onFocus={() => setOpenLocationAuto(true)}
                            label="Select Location"
                            placeholder="Select Location"
                            error={!!locationErrorMessage}
                            helperText={
                              locationErrorMessage ? (
                                <Box component="span" sx={{ minHeight: '24px', display: 'block' }}>
                                  <Typography color="error" variant="body2" align="left" display="block" sx={{ mt: 0, mb: 0, lineHeight: '24px', fontSize: '0.875rem' }}>
                                    Please Select The Location
                                  </Typography>
                                </Box>
                              ) : (
                                <Box component="span" sx={{ minHeight: '24px', display: 'block' }} />
                              )
                            }
                            FormHelperTextProps={{
                              sx: { m: 0, minHeight: '24px', lineHeight: '24px', display: 'block' }
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel>Time</InputLabel>
                    <Select
                      value={time}
                      onChange={handleTimeChange}
                      fullWidth
                      label="Time"
                    >
                      {Array.from({ length: 24 * 2 }, (_, index) => {
                        const hours = Math.floor(index / 2).toString().padStart(2, '0');
                        const minutes = index % 2 === 0 ? '00' : '30';
                        const value = `${hours}:${minutes}`;
                        return (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={3} mt={1}>
                  <Button variant="contained" onClick={handleSave}>Save</Button>
                </Grid>
              </Grid>

              {(status && message) && (
                <Stack direction="row" sx={{ paddingTop: 2 }}>
                  <Alert severity="success">{message}</Alert>
                </Stack>
              )}

              <Grid container spacing={3} alignItems="center" sx={{ marginTop: "1rem", marginLeft:"0rem", width:'100%' }}>
                <Scrollbar>
                  <TableContainer>
                    <Table stickyHeader>
                    <TableHead sx={{ background: (theme) => theme.palette.background.neutral }}>
                      <TableRow sx={{ background: (theme) => theme.palette.background.neutral }}>
                          <TableCell>S.No</TableCell>
                          <TableCell>Client Name</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Time Zone</TableCell>
                          <TableCell>Email Sent Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {clientData.map((row, index) => (
                          <TableRow key={index + 1}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{row.displayname}</TableCell>
                            <TableCell>{row.Location}</TableCell>
                            <TableCell>{row.TimeZone === null ? "No Time Zone" : row.TimeZone}</TableCell>
                            <TableCell>{row.Time}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Page>
    </>
  );
};

export default EmailTimings;
